import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
} from '@mui/material';
import {
  ArrowBackOutlined,
  PictureAsPdfOutlined,
  EditOutlined,
  ShareOutlined,
  WhatsApp,
  EmailOutlined,
  CheckCircleOutline,
  Palette,
  Receipt,
  StarOutline,
  QrCode2,
} from '@mui/icons-material';
import { useFactureStore } from '../stores/useFactureStore';
import { useClientStore } from '../stores/useClientStore';
import { useQuoteStore } from '../stores/useQuoteStore';
import { useAuthStore } from '../stores/useAuthStore';
import { StatutFacture, TypeFacture, Facture } from '../types';
import { formatEuro, formatDate } from '../utils/calculations';
import factureService from '../services/factureService';
import emailService from '../services/emailService';
import CreerAvoirModal from '../components/CreerAvoirModal';
import AvoirsList from '../components/AvoirsList';
import PaymentButton from '../components/PaymentButton';
import { useNotification } from '../contexts/NotificationContext';

const FactureDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const { getFactureById, updateFacture, markFactureAsPaid } = useFactureStore();
  const { getClientById } = useClientStore();
  const { getDevisById } = useQuoteStore();
  const { user } = useAuthStore();
  const { showSuccess, showError } = useNotification();

  const [facture, setFacture] = useState<Facture | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [avoirModalOpen, setAvoirModalOpen] = useState(false);
  const [avoirsKey, setAvoirsKey] = useState(0); // Pour forcer le rafraîchissement de la liste des avoirs
  const [premiumDialogOpen, setPremiumDialogOpen] = useState(false);

  useEffect(() => {
    const loadFacture = async () => {
      if (!idParam) {
        setError('ID de facture manquant');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const id = parseInt(idParam, 10);

        // Essayer d'abord de récupérer depuis le store
        let foundFacture = getFactureById(id);

        // Si pas dans le store, charger depuis l'API
        if (!foundFacture) {
          foundFacture = await factureService.getById(id);
        }

        setFacture(foundFacture);
      } catch (err: any) {
        console.error('Erreur lors du chargement de la facture:', err);
        setError(err.message || 'Erreur lors du chargement de la facture');
      } finally {
        setLoading(false);
      }
    };

    loadFacture();
  }, [idParam, getFactureById]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !facture) {
    return (
      <Box>
        <Alert severity="error">{error || 'Facture non trouvée'}</Alert>
        <Button onClick={() => navigate('/factures')} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography variant="h6" color="text.secondary">Chargement des informations utilisateur...</Typography>
      </Box>
    );
  }

  // Récupérer le client depuis le store si disponible
  const client = getClientById(facture.clientId);
  const devis = getDevisById(facture.devisId);

  // Si le client n'est pas dans le store mais qu'on a clientNom, créer un objet client minimal
  const clientToDisplay = client || (facture.clientNom ? {
    id: facture.clientId,
    nom: facture.clientNom,
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    codePostal: '',
    ville: '',
    dateCreation: '',
    dateModification: '',
  } : null);

  if (!clientToDisplay) {
    return (
      <Box>
        <Alert severity="error">Client non trouvé</Alert>
        <Button onClick={() => navigate('/factures')} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  const handleDownloadPDF = async () => {
    try {
      setIsDownloading(true);
      // Télécharger le PDF depuis le backend
      const pdfBlob = await factureService.downloadPDF(facture.id);

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Facture_${facture.numero}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement du PDF:', error);
      showError('Erreur lors du téléchargement du PDF. Veuillez réessayer.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShareWhatsApp = async () => {
    if (!clientToDisplay.telephone) {
      showError('Numéro de téléphone du client non disponible');
      return;
    }

    setIsSendingWhatsApp(true);
    try {
      // Récupérer le PDF en base64 depuis le backend
      const pdfBase64 = await factureService.getPDFBase64(facture.id);

      const message = `Bonjour ${clientToDisplay.prenom || clientToDisplay.nom},\n\nVoici votre facture ${facture.numero} d'un montant de ${formatEuro(facture.montantTTC)}.\n\nDate d'échéance: ${formatDate(facture.dateEcheance)}\n\nCordialement,\n${user.entreprise}`;
      const phoneNumber = clientToDisplay.telephone.replace(/\s/g, '');

      try {
        // Essayer d'envoyer via l'API backend
        await emailService.sendWhatsApp({
          phoneNumber,
          message,
          pdfBase64,
          filename: `Facture_${facture.numero}.pdf`,
        });
        showSuccess('Message WhatsApp envoyé avec succès !');
      } catch (apiError) {
        // Si l'API échoue, fallback sur le lien WhatsApp classique avec téléchargement du PDF
        console.warn('API WhatsApp non disponible, utilisation du fallback');

        // Télécharger le PDF localement
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${pdfBase64}`;
        link.download = `Facture_${facture.numero}.pdf`;
        link.click();

        // Ouvrir WhatsApp
        setTimeout(() => {
          const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
          window.open(url, '_blank');
          showSuccess('Le PDF a été téléchargé. Sur WhatsApp, cliquez sur l\'icône de trombone (📎) pour joindre le fichier.');
        }, 500);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi WhatsApp:', error);
      showError('Erreur lors de l\'envoi du message WhatsApp');
    } finally {
      setIsSendingWhatsApp(false);
    }
  };

  const handleShareEmail = async () => {
    if (!clientToDisplay.email) {
      showError('Email du client non disponible');
      return;
    }

    setIsSendingEmail(true);
    try {
      // Récupérer le PDF en base64 depuis le backend
      const pdfBase64 = await factureService.getPDFBase64(facture.id);

      const subject = `Facture ${facture.numero} - ${user.entreprise}`;

      // Envoyer via l'API backend (le body sera généré automatiquement côté backend)
      await emailService.sendEmail({
        to: clientToDisplay.email,
        subject,
        pdfBase64,
        filename: `Facture_${facture.numero}.pdf`,
        fromEmail: user.emailProfessionnel || user.email,
        fromName: user.prenom && user.nom ? `${user.prenom} ${user.nom}` : user.nom,
        // Données pour génération automatique du body (FACTURE)
        clientPrenom: clientToDisplay.prenom,
        clientNom: clientToDisplay.nom,
        factureNumero: facture.numero,
        factureMontantTTC: facture.montantTTC,
        factureDateEcheance: facture.dateEcheance,
        entreprise: user.entreprise,
      });

      showSuccess('Email envoyé avec succès !');
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi de l\'email:', error);
      showError(error.message || 'Erreur lors de l\'envoi de l\'email. Vérifiez la configuration du serveur.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      await markFactureAsPaid(facture.id);
      // Recharger la facture pour obtenir les dernières données
      const updatedFacture = getFactureById(facture.id);
      if (updatedFacture) {
        setFacture(updatedFacture);
      }
    } catch (error) {
      console.error('Erreur lors du marquage comme payée:', error);
      showError('Erreur lors du marquage de la facture comme payée');
    }
  };

  const getStatutColor = (statut: StatutFacture) => {
    switch (statut) {
      case StatutFacture.EN_ATTENTE:
        return 'warning';
      case StatutFacture.PAYEE:
        return 'success';
      case StatutFacture.ANNULEE:
        return 'error';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: TypeFacture) => {
    switch (type) {
      case TypeFacture.ACOMPTE:
        return `Facture d'acompte${facture.pourcentageAcompte ? ` (${facture.pourcentageAcompte}%)` : ''}`;
      case TypeFacture.SOLDE:
        return 'Facture de solde';
      case TypeFacture.COMPLETE:
        return 'Facture complète';
      default:
        return type;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#333' }}>
          Facture {facture.numero}
        </Typography>
        <Button
          startIcon={<ArrowBackOutlined />}
          onClick={() => navigate('/factures')}
          variant="outlined"
        >
          Retour
        </Button>
      </Box>

      {/* Statut et actions */}
      <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={facture.statut.replace('_', ' ')}
              color={getStatutColor(facture.statut)}
              sx={{ fontSize: '1rem', fontWeight: 600, px: 2, py: 0.5 }}
            />
            <Chip
              label={getTypeLabel(facture.type)}
              sx={{ fontSize: '1rem', fontWeight: 600, px: 2, py: 0.5, bgcolor: '#e3f2fd', color: '#1976d2' }}
            />
            {facture.statut === StatutFacture.PAYEE && facture.datePaiement && (
              <Chip
                icon={<CheckCircleOutline />}
                label={`Payée le ${formatDate(facture.datePaiement)}`}
                color="success"
                sx={{ fontSize: '1rem', fontWeight: 600, px: 2, py: 0.5 }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
            {/* Bouton de paiement Stripe - Fonctionnalité Premium */}
            {user?.subscriptionTier === 'PREMIUM' ? (
              <PaymentButton
                facture={facture}
                onPaymentCreated={() => {
                  const updatedFacture = getFactureById(facture.id);
                  if (updatedFacture) {
                    setFacture(updatedFacture);
                  }
                }}
              />
            ) : (
              facture.statut !== StatutFacture.PAYEE && (
                <Tooltip title="Fonctionnalité Premium - Cliquez pour en savoir plus">
                  <Button
                    variant="contained"
                    onClick={() => setPremiumDialogOpen(true)}
                    startIcon={
                      <Badge
                        badgeContent={<StarOutline sx={{ fontSize: 14 }} />}
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: '#F59E0B',
                            color: 'white',
                            right: -6,
                            top: -6,
                            border: '2px solid white',
                            padding: '0 3px',
                            minWidth: 20,
                            height: 20,
                          },
                        }}
                      >
                        <QrCode2 />
                      </Badge>
                    }
                    sx={{
                      background: 'linear-gradient(135deg, #635BFF 0%, #4F46E5 100%)',
                      color: 'white',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)',
                      },
                    }}
                  >
                    Générer un lien de paiement
                  </Button>
                </Tooltip>
              )
            )}

            {facture.statut === StatutFacture.EN_ATTENTE && (
              <Button
                variant="contained"
                startIcon={<CheckCircleOutline />}
                onClick={handleMarkAsPaid}
                sx={{
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                }}
              >
                Marquer comme payée
              </Button>
            )}
            {facture.type !== TypeFacture.AVOIR && facture.statut !== StatutFacture.ANNULEE && (
              <Button
                variant="outlined"
                startIcon={<Receipt />}
                onClick={() => setAvoirModalOpen(true)}
                sx={{
                  borderColor: '#dc2626',
                  color: '#dc2626',
                  '&:hover': {
                    borderColor: '#b91c1c',
                    bgcolor: '#fef2f2',
                  },
                }}
              >
                Créer un avoir
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Palette />}
              onClick={() => navigate('/customization')}
              sx={{
                borderColor: '#9333ea',
                color: '#9333ea',
                '&:hover': {
                  borderColor: '#7c3aed',
                  bgcolor: '#faf5ff',
                },
              }}
            >
              Personnaliser
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdfOutlined />}
              onClick={handleDownloadPDF}
              sx={{
                bgcolor: '#374151',
                '&:hover': { bgcolor: '#1F2937' },
              }}
            >
              Télécharger Facture
            </Button>
            <Tooltip title="Partager par WhatsApp">
              <IconButton
                onClick={handleShareWhatsApp}
                disabled={isSendingWhatsApp}
                sx={{
                  bgcolor: '#25D366',
                  color: 'white',
                  '&:hover': { bgcolor: '#1da851' },
                  '&.Mui-disabled': {
                    bgcolor: '#25D36680',
                    color: 'white',
                  },
                }}
              >
                {isSendingWhatsApp ? <CircularProgress size={24} color="inherit" /> : <WhatsApp />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Envoyer par email">
              <IconButton
                onClick={handleShareEmail}
                disabled={isSendingEmail}
                sx={{
                  bgcolor: '#374151',
                  color: 'white',
                  '&:hover': { bgcolor: '#4B5563' },
                  '&.Mui-disabled': {
                    bgcolor: '#37415180',
                    color: 'white',
                  },
                }}
              >
                {isSendingEmail ? <CircularProgress size={24} color="inherit" /> : <EmailOutlined />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Statut de paiement Stripe */}
      {facture.stripePaymentStatus && (
        <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main', mb: 2 }}>
            💳 Paiement en ligne
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">Statut du paiement</Typography>
                <Chip
                  label={
                    facture.stripePaymentStatus === 'SUCCEEDED'
                      ? 'Payé'
                      : facture.stripePaymentStatus === 'PENDING'
                      ? 'En attente'
                      : facture.stripePaymentStatus === 'PROCESSING'
                      ? 'En cours'
                      : facture.stripePaymentStatus === 'FAILED'
                      ? 'Échec'
                      : 'Annulé'
                  }
                  color={
                    facture.stripePaymentStatus === 'SUCCEEDED'
                      ? 'success'
                      : facture.stripePaymentStatus === 'PENDING' || facture.stripePaymentStatus === 'PROCESSING'
                      ? 'warning'
                      : 'error'
                  }
                  sx={{ mt: 0.5, fontWeight: 600 }}
                />
              </Box>
            </Grid>
            {facture.stripePaymentDate && (
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Date de paiement</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                    {formatDate(facture.stripePaymentDate)}
                  </Typography>
                </Box>
              </Grid>
            )}
            {facture.stripePaymentIntentId && (
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="caption" color="text.secondary">ID de transaction</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {facture.stripePaymentIntentId}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Informations client et facture */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main', mb: 2 }}>
              Client
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>
                {clientToDisplay.prenom} {clientToDisplay.nom}
              </strong>
            </Typography>
            {clientToDisplay.adresse && (
              <Typography variant="body2" color="text.secondary">
                {clientToDisplay.adresse}
              </Typography>
            )}
            {(clientToDisplay.codePostal || clientToDisplay.ville) && (
              <Typography variant="body2" color="text.secondary">
                {clientToDisplay.codePostal} {clientToDisplay.ville}
              </Typography>
            )}
            {clientToDisplay.telephone && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Tél: {clientToDisplay.telephone}
              </Typography>
            )}
            {clientToDisplay.email && (
              <Typography variant="body2" color="text.secondary">
                Email: {clientToDisplay.email}
              </Typography>
            )}
            {!client && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 2 }}>
                ⚠️ Informations partielles - Rechargez la page pour voir les détails complets
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main', mb: 2 }}>
              Informations de la facture
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Numéro:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{facture.numero}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Devis associé:</Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'theme.palette.primary.main', cursor: 'pointer' }}
                onClick={() => navigate(`/devis/${facture.devisId}`)}
              >
                {devis?.numero || '-'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Date d'émission:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(facture.dateEmission)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Date d'échéance:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(facture.dateEcheance)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Taux TVA:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{facture.tauxTVA}%</Typography>
            </Box>
            {facture.moyenPaiement && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Moyen de paiement:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{facture.moyenPaiement}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Montants */}
      <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main', mb: 2 }}>
          Détails du montant
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ width: { xs: '100%', sm: '400px' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Montant HT:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatEuro(facture.montantHT)}
              </Typography>
            </Box>

            {/* Afficher la remise si elle existe */}
            {facture.typeRemise && facture.valeurRemise && facture.valeurRemise > 0 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" color="error">
                    Remise ({facture.typeRemise === 'POURCENTAGE' ? `${facture.valeurRemise}%` : 'montant fixe'}):
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                    - {formatEuro(facture.montantRemise || 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>Montant HT après remise:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {formatEuro(facture.montantHTApresRemise || facture.montantHT)}
                  </Typography>
                </Box>
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">TVA ({facture.tauxTVA}%):</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatEuro(facture.montantTVA)}
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                TOTAL TTC:
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                {formatEuro(facture.montantTTC)}
              </Typography>
            </Box>
            {facture.type === TypeFacture.ACOMPTE && facture.pourcentageAcompte && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Acompte de {facture.pourcentageAcompte}% du montant total du devis
              </Alert>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Liste des avoirs */}
      {facture.type !== TypeFacture.AVOIR && (
        <AvoirsList
          key={avoirsKey}
          factureId={facture.id}
          onRefresh={() => {
            // Recharger la facture pour avoir les montants à jour
            const updatedFacture = getFactureById(facture.id);
            if (updatedFacture) {
              setFacture(updatedFacture);
            }
          }}
        />
      )}

      {/* Informations sur la facture originale pour les avoirs */}
      {facture.type === TypeFacture.AVOIR && facture.factureOriginaleId && (
        <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'error.main' }}>
            Avoir créé à partir de la facture
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Facture originale:</strong> {facture.factureOriginaleNumero || `#${facture.factureOriginaleId}`}
            </Typography>
            {facture.conditions && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Motif:</strong> {facture.conditions}
              </Typography>
            )}
          </Alert>
        </Paper>
      )}

      {/* Notes */}
      {facture.notes && (
        <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'theme.palette.primary.main' }}>
            Notes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {facture.notes}
          </Typography>
        </Paper>
      )}

      {/* Modal de création d'avoir */}
      <CreerAvoirModal
        open={avoirModalOpen}
        onClose={() => setAvoirModalOpen(false)}
        facture={facture}
        onAvoirCreated={() => {
          // Recharger la facture et la liste des avoirs
          const updatedFacture = getFactureById(facture.id);
          if (updatedFacture) {
            setFacture(updatedFacture);
          }
          setAvoirsKey((prev) => prev + 1); // Forcer le rafraîchissement de la liste des avoirs
        }}
      />

      {/* Dialog discrète pour informer de la fonctionnalité Premium */}
      <Dialog
        open={premiumDialogOpen}
        onClose={() => setPremiumDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#374151' }}>
          Fonctionnalité Premium
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, color: '#6B7280' }}>
            La génération de liens de paiement par carte bancaire est une fonctionnalité réservée aux abonnés Premium.
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Avec l'offre Premium, vous bénéficiez de :
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2, color: '#374151' }}>
            <li>
              <Typography variant="body2">
                <strong>Paiement client par CB intégré</strong> - Génération de liens de paiement avec QR code
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Signature électronique à distance</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Comptabilité automatisée</strong> - Suivi des dépenses et rapports
              </Typography>
            </li>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setPremiumDialogOpen(false)} color="inherit">
            Fermer
          </Button>
          <Button
            onClick={() => {
              setPremiumDialogOpen(false);
              navigate('/pricing');
            }}
            variant="contained"
            sx={{
              bgcolor: '#F59E0B',
              '&:hover': { bgcolor: '#D97706' },
            }}
          >
            Voir les offres Premium
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FactureDetailPage;
